import type { Transition, Variants } from "framer-motion";

/**
 * Goal-first flow — motion tokens.
 *
 * Single source of truth for durations, easings, stagger intervals, and
 * shared variants. The choreography budget lives in
 * docs/plans/goal-first-ui-plan.md (Motion Choreography Spec) — tune values
 * here, never inline in components. All motion is gated behind
 * useReducedMotion() at the component level.
 */

/** Durations in seconds. Budgets: entrance ≤600ms total, selection ≤800ms. */
export const durations = {
  /** Per-card entrance rise. */
  entrance: 0.45,
  /** Headline fade-up before cards. */
  headline: 0.35,
  /** Selection beat — chosen card wins, siblings yield. */
  selection: 0.5,
} as const;

export const easings = {
  /** Entering elements — one ease-out for the whole flow. */
  out: [0.22, 1, 0.36, 1] as [number, number, number, number],
  /** The one spring — selection beat and toggle settle. */
  spring: { type: "spring", stiffness: 420, damping: 32 } as Transition,
} as const;

/** Stagger intervals in seconds. */
export const stagger = {
  /** ~60ms between card entrances. */
  cards: 0.06,
} as const;

/** Headline fades up first, then cards stagger in beneath it. */
export const headlineVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: durations.headline, ease: easings.out },
  },
};

/** Card entrance: fade + 12px rise, staggered by the container. */
export const cardVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: durations.entrance, ease: easings.out },
  },
  /** Siblings yield on selection: dim, shrink, exit downward. */
  dimmed: {
    opacity: 0,
    y: 16,
    scale: 0.97,
    transition: { duration: durations.selection * 0.6, ease: easings.out },
  },
};

export const cardGridVariants: Variants = {
  hidden: {},
  visible: {
    transition: { delayChildren: durations.headline, staggerChildren: stagger.cards },
  },
};

/** Shared layoutId — selected goal card morphs into the flow-header chip. */
export const GOAL_CHIP_LAYOUT_ID = "goal-chip-morph";
